import { ApolloLink, Observable, FetchResult, Operation } from '@apollo/client';

const MAX_RETRIES = 3;
const BASE_DELAY_MS = 1000;

const CONNECTION_ERROR_MESSAGES = ['Connection closed', 'TIMEOUT_DISCONNECT'];

/**
 * Apollo Link that auto-retries WebSocket subscriptions on connection errors.
 *
 * Why this exists:
 * `aws-appsync-subscription-link` does NOT reconnect when its WebSocket closes.
 * When navigating between pages, the rapid unsubscribe/resubscribe cycle can
 * cause the WebSocket to close, permanently killing all subscriptions.
 * The library is in maintenance mode with no fix planned (see GitHub issue #758).
 *
 * How it works:
 * Wraps the AppSync subscription link and intercepts "Connection closed" errors.
 * On error, it waits briefly then re-subscribes, which causes the underlying
 * link to open a fresh WebSocket. Retries up to 3 times with linear backoff.
 */
export class RetryableSubscriptionLink extends ApolloLink {
  private wrappedLink: ApolloLink;

  constructor(wrappedLink: ApolloLink) {
    super();
    this.wrappedLink = wrappedLink;
  }

  request(operation: Operation): Observable<FetchResult> | null {
    return new Observable<FetchResult>((observer) => {
      let retryCount = 0;
      let currentSubscription: ReturnType<
        Observable<FetchResult>['subscribe']
      > | null = null;
      let retryTimer: ReturnType<typeof setTimeout> | null = null;
      let disposed = false;

      const subscribe = () => {
        if (disposed) return;

        const observable = this.wrappedLink.request(operation);
        if (!observable) {
          observer.complete();
          return;
        }

        currentSubscription = observable.subscribe({
          next: (value) => {
            retryCount = 0;
            observer.next(value);
          },
          error: (err) => {
            if (disposed) return;

            if (retryCount < MAX_RETRIES && isConnectionError(err)) {
              retryCount++;
              const delay = BASE_DELAY_MS * retryCount;
              console.log(
                `[RetryableSubscriptionLink] Connection lost, retry ${retryCount}/${MAX_RETRIES} in ${delay}ms`
              );
              retryTimer = setTimeout(subscribe, delay);
            } else {
              observer.error(err);
            }
          },
          complete: () => {
            if (!disposed) observer.complete();
          }
        });
      };

      subscribe();

      // Cleanup on unsubscribe
      return () => {
        disposed = true;
        if (retryTimer) clearTimeout(retryTimer);
        currentSubscription?.unsubscribe();
      };
    });
  }
}

/**
 * Checks if an error from the AppSync subscription link is a recoverable
 * connection error (WebSocket closed or keep-alive timeout).
 */
function isConnectionError(err: unknown): boolean {
  if (!err || typeof err !== 'object') return false;

  const error = err as Record<string, unknown>;

  // The error shape from aws-appsync-subscription-link is:
  // { errors: [{ message: "Connection closed" }] }
  const errorArrays = [
    error.errors,
    (error.cause as Record<string, unknown> | undefined)?.errors
  ];

  return errorArrays.some(
    (errors) =>
      Array.isArray(errors) &&
      errors.some(
        (e: { message?: string }) =>
          typeof e?.message === 'string' &&
          CONNECTION_ERROR_MESSAGES.some((msg) => e.message!.includes(msg))
      )
  );
}
